import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  CircularProgress, Alert, MenuItem, Select, FormControl, InputLabel,
  Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AttachFile, VideoLibrary, MenuBook, Assignment, Folder
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import {
  createLessonPost, getTeacherLessonPosts, updateLessonPost,
  deleteLessonPost, uploadLessonAttachment
} from '../../api/lessonPosts';

const TYPE_CONFIG = {
  lesson: { label: 'Lesson', icon: <MenuBook />, color: '#F5A623' },
  exercise: { label: 'Exercise', icon: <Assignment />, color: '#4CAF50' },
  resource: { label: 'Resource', icon: <Folder />, color: '#2196F3' },
};

const LessonPosts = () => {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [form, setForm] = useState({
    title: '', description: '', content: '', language: '',
    type: 'lesson', videoUrl: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadLessons();
  }, [user?.id]);

  const loadLessons = async () => {
    if (!user?.id) return;
    try {
      const data = await getTeacherLessonPosts(user.id);
      setLessons(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingLesson(null);
    setForm({ title: '', description: '', content: '', language: '', type: 'lesson', videoUrl: '' });
    setAttachments([]);
    setDialogOpen(true);
  };

  const openEdit = (lesson) => {
    setEditingLesson(lesson);
    setForm({
      title: lesson.title, description: lesson.description,
      content: lesson.content || '', language: lesson.language || '',
      type: lesson.type || 'lesson', videoUrl: lesson.videoUrl || ''
    });
    setAttachments(lesson.attachments || []);
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const result = await uploadLessonAttachment(user.id, file);
        uploaded.push(result);
      }
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      setError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      const lessonData = { ...form, attachments };
      if (editingLesson) {
        await updateLessonPost(editingLesson.id, lessonData);
        setSuccess('Lesson updated!');
      } else {
        await createLessonPost(user.id, lessonData);
        setSuccess('Lesson published!');
      }
      setDialogOpen(false);
      await loadLessons();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lessonId) => {
    try {
      await deleteLessonPost(lessonId);
      setDeleteConfirm(null);
      setSuccess('Lesson deleted');
      await loadLessons();
    } catch (err) {
      setError('Failed to delete');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#F5A623' }} /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>My Lessons</Typography>
          <Typography variant="body2" color="text.secondary">
            Post lessons, exercises, and resources for your students
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' }, fontWeight: 600 }}
        >
          New Lesson
        </Button>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {lessons.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <MenuBook sx={{ fontSize: 60, color: '#F5A623', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No lessons yet</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Create your first lesson to share with your enrolled students
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
          >
            Create Lesson
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {lessons.map(lesson => {
            const config = TYPE_CONFIG[lesson.type] || TYPE_CONFIG.lesson;
            return (
              <Grid item xs={12} md={6} key={lesson.id}>
                <Card sx={{
                  border: '1px solid #eee', transition: 'all 0.2s',
                  '&:hover': { boxShadow: '0 4px 20px rgba(245,166,35,0.15)', borderColor: '#F5A623' }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={config.icon}
                          label={config.label}
                          size="small"
                          sx={{ bgcolor: `${config.color}15`, color: config.color, fontWeight: 600 }}
                        />
                        {lesson.language && (
                          <Chip label={lesson.language} size="small" variant="outlined" />
                        )}
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => openEdit(lesson)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(lesson.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {lesson.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden'
                    }}>
                      {lesson.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(lesson.createdAt)}
                      </Typography>
                      {lesson.attachments?.length > 0 && (
                        <Chip icon={<AttachFile />} label={`${lesson.attachments.length} files`} size="small" variant="outlined" />
                      )}
                      {lesson.videoUrl && (
                        <Chip icon={<VideoLibrary />} label="Video" size="small" variant="outlined" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingLesson ? 'Edit Lesson' : 'Create New Lesson'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth label="Title *" value={form.title}
                onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={form.type} label="Type"
                  onChange={(e) => setForm(p => ({ ...p, type: e.target.value }))}>
                  <MenuItem value="lesson">Lesson</MenuItem>
                  <MenuItem value="exercise">Exercise</MenuItem>
                  <MenuItem value="resource">Resource</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Language" value={form.language}
                onChange={(e) => setForm(p => ({ ...p, language: e.target.value }))}
                placeholder="e.g. Arabic, French"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Video URL (optional)" value={form.videoUrl}
                onChange={(e) => setForm(p => ({ ...p, videoUrl: e.target.value }))}
                placeholder="https://youtube.com/..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Description" multiline rows={2}
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Lesson Content" multiline rows={6}
                value={form.content}
                onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))}
                placeholder="Write your lesson content here..."
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Attachments</Typography>
              <Button
                variant="outlined" component="label" startIcon={<AttachFile />}
                disabled={uploading}
                sx={{ borderColor: '#F5A623', color: '#F5A623', '&:hover': { borderColor: '#e09500' } }}
              >
                {uploading ? 'Uploading...' : 'Add Files'}
                <input type="file" hidden multiple onChange={handleFileUpload} />
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {attachments.map((a, i) => (
                  <Chip
                    key={i} label={a.name} variant="outlined"
                    onDelete={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving && <CircularProgress size={18} />}
            sx={{ bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
          >
            {saving ? 'Saving...' : editingLesson ? 'Update' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Lesson?</DialogTitle>
        <DialogContent>
          <Typography>This action cannot be undone. Your students will no longer see this lesson.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LessonPosts;
